// /subscriptions — admin lista de assinaturas.
// Botão "Nova assinatura" redireciona pra /subscriptions/new (wizard Story 3.2).

import * as Dialog from '@radix-ui/react-dialog';
import { AlertCircle, Play, Plus, Repeat, Search, X, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  CreateSubscriptionDto,
  QuerySubscriptionsDto,
  SubscriptionStatusSchema,
} from '@control-center/shared';
import { formatDayBR } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Select } from '../../components/clients/form-primitives';
import { PillButton } from '../../components/clients/PillButton';
import { EmptyState as RichEmptyState } from '../../components/common/EmptyState';
import { PageHelp } from '../../components/common/PageHelp';
import { CancelSubscriptionModal } from '../../components/subscriptions/CancelSubscriptionModal';
import { PendingCancellationBanner } from '../../components/subscriptions/PendingCancellationBanner';
import { SubscriptionForm } from '../../components/subscriptions/SubscriptionForm';
import { useClientsInfinite } from '../../hooks/use-clients';
import { usePlans } from '../../hooks/use-plans';
import {
  useCreateSubscription,
  useReactivateSubscription,
  useSubscriptions,
} from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';
import type { SubscriptionLite } from '../../lib/subscriptions-api';

const STATUS_LABEL: Record<SubscriptionStatusSchema, string> = {
  ativa: 'Ativa',
  em_teste: 'Em teste',
  trial_expired: 'Trial expirado',
  aguardando_pagamento: 'Aguardando pgto',
  inadimplente: 'Inadimplente',
  suspensa: 'Suspensa',
  pending_cancellation: 'Cancelando',
  cancelada: 'Cancelada',
  pending_provisioning: 'Provisionando',
  encerrada: 'Encerrada',
};

const STATUS_DOT: Record<SubscriptionStatusSchema, string> = {
  ativa: 'bg-good ring-good/15',
  em_teste: 'bg-warn ring-warn/15',
  trial_expired: 'bg-bad ring-bad/15',
  aguardando_pagamento: 'bg-warn ring-warn/15',
  inadimplente: 'bg-bad ring-bad/15',
  suspensa: 'bg-bad ring-bad/15',
  pending_cancellation: 'bg-warn ring-warn/15',
  cancelada: 'bg-bad ring-bad/15',
  pending_provisioning: 'bg-muted-2 ring-muted-3/15',
  encerrada: 'bg-muted-2 ring-muted-3/15',
};

export function SubscriptionsPage(): JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | SubscriptionStatusSchema>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<SubscriptionLite | null>(null);

  const filters: QuerySubscriptionsDto = useMemo(() => {
    const out: QuerySubscriptionsDto = { limit: 50 };
    if (statusFilter !== 'all') out.status = statusFilter;
    return out;
  }, [statusFilter]);

  const list = useSubscriptions(filters);
  const clientsQ = useClientsInfinite();
  const plansQ = usePlans({ limit: 100 });
  const create = useCreateSubscription();
  const reactivate = useReactivateSubscription();

  async function handleReactivate(sub: SubscriptionLite): Promise<void> {
    const ok = window.confirm(
      `Reativar assinatura de ${sub.client.name}? Próxima cobrança fica para daqui a um ciclo.`,
    );
    if (!ok) return;
    try {
      await reactivate.mutateAsync({ id: sub.id, payload: { reason: null } });
      toast.success('Assinatura reativada.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos reativar.'));
    }
  }

  const allClients = useMemo(
    () => clientsQ.data?.pages.flatMap((p) => p.items) ?? [],
    [clientsQ.data],
  );

  const filteredItems = useMemo(() => {
    const items = list.data?.items ?? [];
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter(
      (s) =>
        s.client.name.toLowerCase().includes(needle) ||
        s.plan?.name.toLowerCase().includes(needle) ||
        s.plan?.slug.toLowerCase().includes(needle),
    );
  }, [list.data, q]);

  async function handleSubmit(values: CreateSubscriptionDto): Promise<void> {
    try {
      await create.mutateAsync(values);
      toast.success('Assinatura criada.');
      setFormOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos criar a assinatura.'));
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">Assinaturas</h1>
            <p className="mt-1 text-[13px] text-muted">
              Owl-managed (geridas pelo Control Center).
            </p>
          </div>
          <PageHelp id="subscriptions" />
        </div>
        <PillButton
          solid
          icon={<Plus size={14} strokeWidth={2} />}
          onClick={() => navigate('/subscriptions/new')}
        >
          Nova assinatura
        </PillButton>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Field label="Buscar">
            {({ inputId }) => (
              <div className="relative">
                <Search
                  size={14}
                  strokeWidth={2}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
                />
                <Input
                  id={inputId}
                  type="text"
                  placeholder="Cliente ou plano"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9"
                />
              </div>
            )}
          </Field>
        </div>
        <div className="min-w-[180px]">
          <Field label="Status">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as 'all' | SubscriptionStatusSchema)
                }
              >
                <option value="all">Todos</option>
                {Object.entries(STATUS_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </div>

      {list.isLoading ? (
        <SkeletonList />
      ) : list.isError ? (
        <ErrorBox
          message={extractErrorMessage(list.error, 'Não conseguimos carregar.')}
          onRetry={() => void list.refetch()}
        />
      ) : filteredItems.length > 0 ? (
        <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
          <table className="w-full text-[13px]">
            <thead className="border-b border-line bg-panel-2">
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-2">
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Plano</th>
                <th className="px-4 py-3 font-medium tnum">Preço</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium tnum">Início</th>
                <th className="px-4 py-3 font-medium tnum">Próxima</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((s) => (
                <SubscriptionRow
                  key={s.id}
                  sub={s}
                  onCancel={setCancelTarget}
                  onReactivate={(sub) => void handleReactivate(sub)}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          onAdd={() => navigate('/subscriptions/new')}
          hasFilters={Boolean(q || statusFilter !== 'all')}
        />
      )}

      <Dialog.Root open={formOpen} onOpenChange={setFormOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            className={cn(
              'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            )}
          />
          <Dialog.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
              'w-[min(640px,calc(100vw-32px))] max-h-[calc(100vh-48px)] overflow-y-auto',
              'rounded-[14px] border border-line bg-panel shadow-lg p-7',
              'focus:outline-none',
            )}
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <Dialog.Title className="text-[20px] font-semibold tracking-[-0.01em] text-ink">
                Nova assinatura
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Fechar"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
                >
                  <X size={15} strokeWidth={2} />
                </button>
              </Dialog.Close>
            </div>

            <SubscriptionForm
              clients={allClients}
              plans={plansQ.data?.items ?? []}
              isPending={create.isPending}
              onSubmit={handleSubmit}
              onCancel={() => setFormOpen(false)}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <CancelSubscriptionModal
        subscription={cancelTarget}
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
      />
    </div>
  );
}

function SubscriptionRow({
  sub,
  onCancel,
  onReactivate,
}: {
  sub: SubscriptionLite;
  onCancel: (sub: SubscriptionLite) => void;
  onReactivate: (sub: SubscriptionLite) => void;
}): JSX.Element {
  const canCancel = sub.status !== 'cancelada' && sub.status !== 'pending_cancellation';
  const canReactivate = sub.status === 'cancelada' || sub.status === 'encerrada';

  return (
    <>
      <tr className="border-b border-line last:border-b-0 hover:bg-panel-2">
        <td className="px-4 py-3 text-ink">{sub.client.name}</td>
        <td className="px-4 py-3 text-muted">
          {sub.plan ? (
            <>
              {sub.plan.name}
              {sub._count.entitlements > 0 && (
                <span className="ml-1 text-[11px] text-muted-2">
                  ({sub._count.entitlements} SaaS)
                </span>
              )}
            </>
          ) : (
            // Story 4.13 — recorrente standalone (sem plano): mostra a descrição + badge.
            <span className="inline-flex items-center gap-2">
              <span className="text-ink-2">{sub.description?.trim() || 'Cobrança recorrente'}</span>
              <span className="rounded-[5px] border border-line-2 bg-panel-2 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-2">
                Sem plano
              </span>
            </span>
          )}
        </td>
        <td className="px-4 py-3 tnum text-ink">
          {formatBRL(sub.price)}
          <span className="ml-1 text-[11px] text-muted-2">/{shortCycle(sub)}</span>
        </td>
        <td className="px-4 py-3">
          <span
            className={cn(
              'inline-flex items-center gap-[6px] text-[11px] font-medium',
              sub.status === 'ativa' || sub.status === 'em_teste' ? 'text-ink-2' : 'text-muted',
            )}
          >
            <span
              aria-hidden
              className={cn('h-[7px] w-[7px] rounded-full ring-[3px]', STATUS_DOT[sub.status])}
            />
            {STATUS_LABEL[sub.status]}
          </span>
        </td>
        <td className="px-4 py-3 tnum text-[12px] text-muted-2">{formatDate(sub.startDate)}</td>
        <td className="px-4 py-3 tnum text-[12px] text-muted-2">
          {sub.nextBillingDate ? formatDate(sub.nextBillingDate) : '—'}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-1">
            {canCancel && (
              <button
                type="button"
                aria-label={`Cancelar assinatura ${sub.client.name}`}
                onClick={() => onCancel(sub)}
                className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-bad"
                title="Cancelar"
              >
                <XCircle size={14} strokeWidth={1.8} />
              </button>
            )}
            {canReactivate && (
              <button
                type="button"
                aria-label={`Reativar assinatura ${sub.client.name}`}
                onClick={() => onReactivate(sub)}
                className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-good"
                title="Reativar"
              >
                <Play size={14} strokeWidth={1.8} />
              </button>
            )}
          </div>
        </td>
      </tr>
      {sub.status === 'pending_cancellation' && (
        <tr className="border-b border-line bg-panel-2/50">
          <td colSpan={7} className="px-4 py-2">
            <PendingCancellationBanner
              subscriptionId={sub.id}
              pendingCancellationAt={sub.nextBillingDate}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function SkeletonList(): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          aria-hidden
          className="h-[56px] animate-pulse border-b border-line bg-panel-2 last:border-b-0"
        />
      ))}
    </div>
  );
}

function ErrorBox({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/30 bg-bad/5 p-5 text-[13px]">
      <div className="flex items-center gap-2 font-medium text-bad">
        <AlertCircle size={14} strokeWidth={2} aria-hidden />
        Não conseguimos carregar as assinaturas.
      </div>
      <p className="mt-1 text-muted">{message}</p>
      <div className="mt-3">
        <PillButton onClick={onRetry}>Tentar de novo</PillButton>
      </div>
    </div>
  );
}

function EmptyState({
  onAdd,
  hasFilters,
}: {
  onAdd: () => void;
  hasFilters: boolean;
}): JSX.Element {
  if (hasFilters) {
    return (
      <div className="rounded-[14px] border border-line bg-panel-2 p-6 text-center">
        <p className="text-[13px] text-muted">Nenhuma assinatura bate com os filtros.</p>
      </div>
    );
  }
  return (
    <RichEmptyState
      icon={Repeat}
      title="Sem assinaturas ativas"
      description="Assinatura é o motor recorrente — vincula cliente a plano e gera cobrança automática todo mês."
      bullets={[
        'Cobra sozinha no dia que você escolher (1-28)',
        'Cliente paga, NF emite automática (se plano tiver auto)',
        'Régua de inadimplência dispara sozinha após vencimento',
      ]}
      primaryAction={{
        label: 'Nova assinatura',
        icon: <Plus size={14} strokeWidth={2} />,
        onClick: onAdd,
      }}
      secondaryAction={{
        label: 'Cobrança avulsa (sem recorrência)',
        href: '/charges/new',
      }}
    />
  );
}

function formatBRL(value: string | number): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return 'R$ —';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// startDate/nextBillingDate são dia puro (@db.Date): formatBR aplicava fuso e
// mostrava o dia anterior (Gisele: 26/08 aparecia 25/08 aqui e 26/08 no detalhe).
function formatDate(iso: string): string {
  return formatDayBR(iso);
}

function shortCycle(sub: SubscriptionLite): string {
  // Sem campo billing_cycle direto na lista — inferimos do plan + price ratio.
  // Pra MVP, mostro genérico baseado em billingDay (mensal se tem billing_day).
  if (sub.billingDay === null) return 'único';
  return 'mês';
}
